<script setup>
import { computed } from 'vue'
import { uuid as newUuid } from '@addictivegroup/addictive-offline'
import { useMeta } from '@/stores/meta.js'
import { formatPhone } from '@/lib/phone.js'
import { CHANNELS, kindsFor } from '@/lib/contactPoints.js'

/**
 * E-mails and phones of an account or contact (D-40): any number, one main one per channel. A row
 * is an e-mail, a phone or another channel (its icon, its input); its kind says what it is for —
 * work, private, a secretariat, the company's main one (D-229, D-238) — from the dictionary
 * `contact_point_kind` (D-136), the kinds that fit its channel. "+ E-mail", "+ Phone" add a work
 * one unless changed.
 */
// `country`: where a number typed without a prefix is (an account's country of registration).
const props = defineProps({ modelValue: { type: Array, default: () => [] }, disabled: Boolean, errors: { type: Object, default: () => ({}) }, country: { type: String, default: null } })
const emit = defineEmits(['update:modelValue'])
const meta = useMeta()
const ICONS = { email: 'fa-envelope', phone: 'fa-phone', other: 'fa-link' }
const kindsOf = (channel) => kindsFor(meta, channel)
const defaultKind = (channel) => {
  const kinds = kindsOf(channel)
  const wanted = channel === 'other' ? 'other' : 'work'
  return (kinds.find((k) => k.key === wanted) || kinds[0])?.key || wanted
}
const addable = computed(() => CHANNELS.filter((c) => kindsOf(c).length))

function update(i, patch) {
  const list = props.modelValue.map((p, j) => (j === i ? { ...p, ...patch } : p))
  if (patch.is_primary) list.forEach((p, j) => { if (j !== i && p.channel === list[i].channel) p.is_primary = false })
  emit('update:modelValue', list)
}
function add(channel) {
  const hasChannel = props.modelValue.some((p) => p.channel === channel)
  emit('update:modelValue', [...props.modelValue, { uuid: newUuid(), channel, kind: defaultKind(channel), value: '', label: null, is_primary: !hasChannel }])
}
function remove(i) {
  emit('update:modelValue', props.modelValue.filter((_, j) => j !== i))
}
</script>

<template>
  <div class="space-y-2">
    <div v-for="(p, i) in modelValue" :key="p.uuid || i" class="flex items-center gap-2" :data-channel="p.channel">
      <i :class="['fa-solid fa-fw shrink-0 text-faint', ICONS[p.channel] || ICONS.other]" :title="$t(`dictionaries.channels.${p.channel}`)" aria-hidden="true"></i>
      <select class="input w-36 shrink-0 sm:w-44" :value="p.kind" :disabled="disabled" :aria-label="$t('record.pointKind')" :data-testid="`contact-point-kind-${i}`" @change="update(i, { kind: $event.target.value })">
        <option v-for="k in kindsOf(p.channel)" :key="k.key" :value="k.key">{{ k.label }}</option>
        <option v-if="!kindsOf(p.channel).some((k) => k.key === p.kind)" :value="p.kind">{{ meta.dictionaryLabel('contact_point_kind', p.kind) }}</option>
      </select>
      <input
        :class="['input min-w-0 flex-1', errors[`contact_points.${i}.value`] ? 'invalid' : '']"
        :type="p.channel === 'email' ? 'email' : p.channel === 'phone' ? 'tel' : 'text'"
        :value="p.value"
        :disabled="disabled"
        :placeholder="p.channel === 'email' ? $t('address.emailExample') : p.channel === 'phone' ? '+48 …' : ''"
        @input="update(i, { value: $event.target.value })"
        @blur="p.channel === 'phone' && p.value && formatPhone(p.value, country) !== p.value && update(i, { value: formatPhone(p.value, country) })"
      />
      <input class="input hidden w-28 sm:block" :value="p.label || ''" :placeholder="$t('common.label')" :disabled="disabled" @input="update(i, { label: $event.target.value || null })" />
      <button type="button" :title="$t('record.primary')" :class="['rounded p-1.5', p.is_primary ? 'text-amber-500' : 'text-faint hover:text-amber-500']" :disabled="disabled" @click="update(i, { is_primary: true })">
        <i class="fa-solid fa-star" aria-hidden="true"></i>
      </button>
      <button v-if="!disabled" type="button" class="rounded p-1.5 text-faint hover:text-red-600" :aria-label="$t('common.remove')" @click="remove(i)"><i class="fa-solid fa-trash-can" aria-hidden="true"></i></button>
    </div>
    <div v-if="!disabled" class="flex flex-wrap items-center gap-3">
      <button v-for="c in addable" :key="c" type="button" class="link text-sm" :data-testid="`contact-point-add-${c}`" @click="add(c)"><i class="fa-solid fa-plus text-xs" aria-hidden="true"></i> {{ $t(`record.addPoint.${c}`) }}</button>
    </div>
  </div>
</template>
